import { useCallback, useEffect, useRef, useState } from "react";
import adminApi from "../api/adminApi";
import {
  createMailingButton,
  INITIAL_MAILING_DATA
} from "../utils/defaults";
import {
  moveArrayItemById,
  removeArrayItemById,
  updateArrayItemById
} from "../utils/arrayEditors";

export function useAdminMailing({ adminToken, enabled, stats }) {
  const [mailingData, setMailingData] = useState(INITIAL_MAILING_DATA);
  const [mailingImages, setMailingImages] = useState([]);
  const [mailingButtons, setMailingButtons] = useState([]);
  const [mailingSending, setMailingSending] = useState(false);
  const [mailingResult, setMailingResult] = useState(null);
  const [mailingFileKey, setMailingFileKey] = useState(0);
  const [mailingProgress, setMailingProgress] = useState(null);
  const [mailingStartFromUserId, setMailingStartFromUserId] = useState("");
  const [mailingRate, setMailingRate] = useState(50);
  const [mailingPreview, setMailingPreview] = useState(null);

  const mailingProgressTimer = useRef(null);
  const mailingPreviewTimer = useRef(null);

  const stopMailingProgress = useCallback(() => {
    if (mailingProgressTimer.current) {
      clearInterval(mailingProgressTimer.current);
      mailingProgressTimer.current = null;
    }
  }, []);

  useEffect(() => () => stopMailingProgress(), [stopMailingProgress]);

  const fetchMailingProgress = useCallback(async (jobId) => {
    if (!adminToken) return;
    try {
      const res = await adminApi.getBroadcastStatus(adminToken, jobId);
      const data = res.data;
      if (!data || data.status === "idle") {
        setMailingProgress(null);
        stopMailingProgress();
        return;
      }

      setMailingProgress(data);

      if (data.status === "done" || data.status === "error") {
        stopMailingProgress();
        setMailingResult(
          data.status === "done"
            ? {
                success: true,
                message: `Рассылка завершена. Успешно: ${data.sent}, Ошибки: ${data.failed}, Всего: ${data.total}`
              }
            : {
                success: false,
                message: data.error || "Ошибка при выполнении рассылки"
              }
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [adminToken, stopMailingProgress]);

  const startMailingProgress = useCallback((jobId) => {
    stopMailingProgress();
    fetchMailingProgress(jobId);
    mailingProgressTimer.current = setInterval(() => fetchMailingProgress(jobId), 1000);
  }, [fetchMailingProgress, stopMailingProgress]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    startMailingProgress();
  }, [enabled, adminToken, startMailingProgress]);

  const fetchMailingPreview = useCallback(async (target, startFromId) => {
    if (!adminToken) return;
    try {
      const res = await adminApi.getBroadcastPreview(adminToken, {
        target,
        startFromUserId: startFromId ? Number(startFromId) : 0
      });
      setMailingPreview(res.data || null);
    } catch (error) {
      console.error(error);
      setMailingPreview(null);
    }
  }, [adminToken]);

  useEffect(() => {
    if (!enabled || !adminToken) return undefined;

    if (mailingPreviewTimer.current) {
      clearTimeout(mailingPreviewTimer.current);
    }

    mailingPreviewTimer.current = setTimeout(() => {
      fetchMailingPreview(mailingData.target, mailingStartFromUserId);
    }, 300);

    return () => {
      if (mailingPreviewTimer.current) {
        clearTimeout(mailingPreviewTimer.current);
        mailingPreviewTimer.current = null;
      }
    };
  }, [enabled, adminToken, mailingData.target, mailingStartFromUserId, fetchMailingPreview]);

  const reset = useCallback(() => {
    setMailingData(INITIAL_MAILING_DATA);
    setMailingImages([]);
    setMailingButtons([]);
    setMailingResult(null);
    setMailingProgress(null);
    setMailingFileKey((value) => value + 1);
  }, []);

  const setImagesFromEvent = useCallback((event) => {
    const files = Array.from(event.target.files || []).slice(0, 10);
    setMailingImages(files);
  }, []);

  const addButton = useCallback((type, initial = null) => {
    const button = createMailingButton(type, initial && typeof initial === "object" ? initial : {});
    setMailingButtons((prev) => [...prev, button]);
    return button.id;
  }, []);

  const updateButton = useCallback((id, patch) => {
    setMailingButtons((prev) => updateArrayItemById(prev, id, patch));
  }, []);

  const moveButton = useCallback((id, direction) => {
    setMailingButtons((prev) => moveArrayItemById(prev, id, direction));
  }, []);

  const removeButton = useCallback((id) => {
    setMailingButtons((prev) => removeArrayItemById(prev, id));
  }, []);

  const getEstimatedTotal = useCallback(() => {
    if (mailingPreview && Number.isFinite(mailingPreview.remaining)) {
      return Number(mailingPreview.remaining) || 0;
    }
    if (mailingData.target === "test") return 1;
    if (mailingData.target === "conversations") return 2000;
    if (mailingData.target === "donates") return Number(stats?.premiumUsers || 0);
    if (mailingData.target === "new") {
      const registrations = Array.isArray(stats?.registrations) ? stats.registrations : [];
      return registrations.reduce((sum, value) => sum + Number(value || 0), 0);
    }
    return Number(stats?.totalUsers || 0);
  }, [mailingData.target, mailingPreview, stats]);

  const getEstimatedEtaSec = useCallback(() => {
    const total = getEstimatedTotal();
    const rate = Math.max(1, Number(mailingRate || 1));
    if (!total || total <= 0) return null;
    return Math.ceil(total / rate);
  }, [getEstimatedTotal, mailingRate]);

  const send = useCallback(async () => {
    if (!adminToken) {
      window.alert("Нет adminToken");
      return;
    }

    const hasText = mailingData.text && mailingData.text.trim().length > 0;
    const attachmentsValue = String(mailingData.attachments || "").trim();
    const hasAttachments = attachmentsValue.length > 0;

    if (!hasText && mailingImages.length === 0 && !hasAttachments) {
      window.alert("Введите текст рассылки или добавьте изображения");
      return;
    }

    const invalidButton = mailingButtons.find((button) => !button.text.trim() || !button.value.trim());
    if (invalidButton) {
      window.alert("Заполните текст и значение для всех кнопок");
      return;
    }

    setMailingSending(true);
    setMailingResult(null);
    setMailingProgress(null);

    try {
      const formData = new FormData();
      formData.append("text", mailingData.text);
      formData.append("target", mailingData.target);
      if (mailingStartFromUserId) {
        formData.append("startFromUserId", String(mailingStartFromUserId).trim());
      }
      formData.append("ratePerSec", String(Math.max(1, Number(mailingRate || 1))));
      if (hasAttachments) {
        formData.append("attachments", attachmentsValue);
      }
      if (mailingButtons.length > 0) {
        formData.append(
          "buttons",
          JSON.stringify(
            mailingButtons.map((button) => ({
              type: button.type,
              text: button.text,
              value: button.value
            }))
          )
        );
      }
      mailingImages.forEach((file) => formData.append("images", file));

      const res = await adminApi.startBroadcast(adminToken, formData);
      const jobId = res.data?.jobId;
      const total = res.data?.total ?? 0;
      const status = res.data?.status || "running";

      setMailingProgress({
        id: jobId,
        total,
        sent: 0,
        failed: 0,
        processed: 0,
        percent: total > 0 ? 0 : 100,
        status
      });

      if (status === "done") {
        setMailingResult({
          success: true,
          message: `Рассылка завершена. Успешно: 0, Ошибки: 0, Всего: ${total}`
        });
      } else {
        startMailingProgress(jobId);
      }
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.error || "Ошибка при отправке рассылки";
      setMailingResult({ success: false, message });
    } finally {
      setMailingSending(false);
    }
  }, [
    adminToken,
    mailingButtons,
    mailingData,
    mailingImages,
    mailingRate,
    mailingStartFromUserId,
    startMailingProgress
  ]);

  return {
    state: {
      mailingData,
      mailingImages,
      mailingButtons,
      mailingSending,
      mailingResult,
      mailingFileKey,
      mailingProgress,
      mailingStartFromUserId,
      mailingRate,
      mailingPreview,
      mailingDisabled: mailingSending || mailingProgress?.status === "running",
      isMailingRunning: mailingProgress?.status === "running"
    },
    selectors: {
      getEstimatedTotal,
      getEstimatedEtaSec
    },
    actions: {
      setMailingData,
      setMailingStartFromUserId,
      setMailingRate,
      setImagesFromEvent,
      addButton,
      updateButton,
      moveButton,
      removeButton,
      reset,
      send
    }
  };
}

export default useAdminMailing;
